import {Component, DoCheck, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {NotificationService} from '../services/notification.service';
import {ModelService} from '../api/services/model.service';
import {MapService} from '../api/services/map.service';
import {LegendService} from '../api/services/legend.service';
import {TextInputComponent} from '../shared/forms/text-input/text-input.component';
import {TextAreaComponent} from '../shared/forms/text-area/text-area-input.component';
import {SlideToggleInputComponent} from '../shared/forms/slide-select/slide-toggle-input.component';
import {
  MaporiumAccordionItemComponent
} from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CreateModelDto, InclusionOptions, ModelDto, ModelElementType, UpdateModelDto} from '@maporium-workspace/shared';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {DashboardComponent} from '../dashboard/dashboard.component';

export interface CreateModelDialogData {
  selectedElement?: any; // The selected node or link (for creation)
  elementType?: ModelElementType; // For creation
  mapId?: string; // For creation
  model?: ModelDto; // For editing
}

@Component({
  selector: 'maporium-create-model-dialog',
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="material-symbols-rounded me-2">{{ isEditMode ? 'edit' : 'add_circle' }}</span>
        {{ (isEditMode ? 'MODEL_DIALOG.UPDATE_TITLE' : 'MODEL_DIALOG.CREATE_TITLE') | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>

    <div mat-dialog-content>
      <form [formGroup]="myForm">
        <div class="mb-3">
          <app-text-input
            [label]="'CREATE_EDIT_SMAP.FIELDS.NAME' | translate"
            formControlName="name"
            [autoFocusAndSelect]="true">
          </app-text-input>
          <app-text-area
            [label]="'CREATE_EDIT_SMAP.FIELDS.DESCRIPTION' | translate"
            formControlName="description">
          </app-text-area>
        </div>

        <maporium-accordion-item [title]="'FILTER_DIALOG.CONFIGURATION' | translate" [sections]="1">
          <div class="inclusion-options">
            <app-slide-toggle-input
              [label]="'MODEL_DIALOG.EXCLUDE_FROM_LEGEND' | translate"
              [tooltip]="'MODEL_DIALOG.EXCLUDE_FROM_LEGEND_TOOLTIP' | translate"
              formControlName="excludeFromLegend">
            </app-slide-toggle-input>
          </div>
        </maporium-accordion-item>

        <maporium-accordion-item [title]="'FILTER_DIALOG.DEFINITION' | translate" [sections]="6">
          <div class="inclusion-options">
            <app-slide-toggle-input
              [label]="getIncludeNameLabel()"
              [tooltip]="'MODEL_DIALOG.INCLUDE_NAME_TOOLTIP' | translate"
              formControlName="includeName">
            </app-slide-toggle-input>

            <app-slide-toggle-input
              [label]="getIncludeDescriptionLabel()"
              [tooltip]="'MODEL_DIALOG.INCLUDE_DESCRIPTION_TOOLTIP' | translate"
              formControlName="includeDescription">
            </app-slide-toggle-input>

            <app-slide-toggle-input
              [label]="'MODEL_DIALOG.INCLUDE_PROPERTIES' | translate"
              [tooltip]="'MODEL_DIALOG.INCLUDE_PROPERTIES_TOOLTIP' | translate"
              formControlName="includeProperties">
            </app-slide-toggle-input>

            <app-slide-toggle-input
              [label]="'MODEL_DIALOG.INCLUDE_PROPERTY_VALUES' | translate"
              [tooltip]="'MODEL_DIALOG.INCLUDE_PROPERTY_VALUES_TOOLTIP' | translate"
              formControlName="includePropertyValues"
              *ngIf="myForm.get('includeProperties')?.value">
            </app-slide-toggle-input>

            <app-slide-toggle-input
              [label]="'MODEL_DIALOG.ERASE_UNRELATED' | translate"
              [tooltip]="'MODEL_DIALOG.ERASE_UNRELATED_TOOLTIP' | translate"
              formControlName="eraseUnrelatedProperties"
              *ngIf="myForm.get('includeProperties')?.value">
            </app-slide-toggle-input>

            <app-slide-toggle-input
              [label]="'MODEL_DIALOG.INCLUDE_STYLES' | translate"
              [tooltip]="'MODEL_DIALOG.INCLUDE_STYLES_TOOLTIP' | translate"
              formControlName="includeStyles">
            </app-slide-toggle-input>
          </div>
        </maporium-accordion-item>

        <ng-container *ngIf="showUpdateFromSelectionFocus">
          <p class="mt-3"></p>
          <app-slide-toggle-input
            [label]="'MODEL_DIALOG.UPDATE_FROM_SELECTION_LABEL' | translate"
            [matTooltip]="'MODEL_DIALOG.UPDATE_FROM_SELECTION_TOOLTIP' | translate"
            formControlName="updateFromSelectionFocus">
          </app-slide-toggle-input>
        </ng-container>
      </form>
    </div>

    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close [disabled]="isCreating">{{ 'FILTER_DIALOG.ACTIONS.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary"
              [disabled]="!myForm.valid || isCreating"
              (click)="create()">
        {{ (isCreating ? (isEditMode ? 'MODEL_DIALOG.UPDATING' : 'MODEL_DIALOG.CREATING') : (isEditMode ? 'FILTER_DIALOG.ACTIONS.UPDATE' : 'FILTER_DIALOG.ACTIONS.CREATE')) | translate }}
      </button>
    </div>
  `,
  styleUrls: ['./create-model-dialog.component.scss'],
  imports: [
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    TextInputComponent,
    TextAreaComponent,
    SlideToggleInputComponent,
    MaporiumAccordionItemComponent,
    TranslatePipe
  ],
  standalone: true
})
export class CreateModelDialogComponent implements OnInit, DoCheck {
  myForm: FormGroup;
  isCreating = false;
  isEditMode = false;
  showUpdateFromSelectionFocus = false;

  constructor(
    private fb: FormBuilder,
    private modelService: ModelService,
    private mapService: MapService,
    private legendService: LegendService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<CreateModelDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateModelDialogData
  ) {
    this.isEditMode = !!this.data.model;

    // For edit mode, use existing model data
    // For create mode, use selected element data
    const sourceData = this.isEditMode ? this.data.model : this.data.selectedElement;
    const inclusionOptions = this.isEditMode ? this.data.model!.inclusionOptions : null;

    this.myForm = this.fb.group({
      name: [sourceData?.name || '', Validators.required],
      description: [sourceData?.description || ''],
      includeName: [inclusionOptions?.includeName ?? false],
      includeDescription: [inclusionOptions?.includeDescription ?? false],
      includeProperties: [inclusionOptions?.includeProperties ?? true],
      includePropertyValues: [inclusionOptions?.includePropertyValues ?? false],
      includeStyles: [inclusionOptions?.includeStyles ?? true],
      eraseUnrelatedProperties: [inclusionOptions?.eraseUnrelatedProperties ?? false],
      excludeFromLegend: [false],
      // MAP-1569: off by default; only rendered while the canvas' current selection
      // focus is a node/link matching this model's elementType (kept in sync via ngDoCheck).
      updateFromSelectionFocus: [false]
    });

    // In edit mode, check if model is excluded from legend
    if (this.isEditMode && this.data.model) {
      const legend = this.legendService.getCurrentLegend();
      if (legend?.legendModels) {
        const lm = legend.legendModels.find(l => l.modelId === this.data.model!.id);
        if (lm) {
          this.myForm.patchValue({excludeFromLegend: !lm.included});
        }
      }
    }
  }

  ngOnInit(): void {}

  /**
   * MAP-1569: the model dialog can be opened with hasBackdrop:false, so the
   * canvas selection can change while the dialog stays open. Keep the toggle's
   * visibility in sync with the current selection focus on every CD tick, before
   * the view is checked (so no ExpressionChangedAfterItHasBeenChecked), and only
   * touch anything when the answer actually flips.
   */
  ngDoCheck(): void {
    if (!this.isEditMode) return;

    const matches = this.selectionFocusMatchesModelType();
    if (matches === this.showUpdateFromSelectionFocus) return;

    this.showUpdateFromSelectionFocus = matches;
    if (!matches) {
      // Hidden toggles must not silently apply on save with a stale selection.
      this.myForm.get('updateFromSelectionFocus')?.setValue(false, {emitEvent: false});
    }
  }

  /**
   * "Selection focus" = DashboardComponent.tappedNode/tappedLink, the same
   * concept scene position/scale updates already key off. Hidden when the
   * selection is empty or its type (node/link) doesn't match this model's type.
   */
  private selectionFocusMatchesModelType(): boolean {
    const elementType = this.data.model?.elementType;
    if (!elementType) return false;
    if (elementType === ModelElementType.NODE) {
      return !!DashboardComponent.tappedNode;
    }
    if (elementType === ModelElementType.LINK) {
      return !!DashboardComponent.tappedLink;
    }
    return false;
  }

  private getSelectionFocusElementId(): string | undefined {
    if (!this.data.model) return undefined;
    return this.data.model.elementType === ModelElementType.NODE
      ? DashboardComponent.tappedNode?.id
      : DashboardComponent.tappedLink?.id;
  }

  getIncludeNameLabel(): string {
    // For edit mode, use the template element data from elementDefinition
    // For create mode, use the selected element
    const sourceData = this.isEditMode
      ? this.data.model?.elementDefinition?.originalElement
      : this.data.selectedElement;

    const name = sourceData?.name;
    if (!name || name.trim() === '') {
      return this.translate.instant('MODEL_DIALOG.INCLUDE_NAME');
    }
    const truncated = name.length > 32 ? name.substring(0, 29) + '...' : name;
    return this.translate.instant('MODEL_DIALOG.INCLUDE_NAME_WITH_VALUE', { name: truncated });
  }

  getIncludeDescriptionLabel(): string {
    // For edit mode, use the template element data from elementDefinition
    // For create mode, use the selected element
    const sourceData = this.isEditMode
      ? this.data.model?.elementDefinition?.originalElement
      : this.data.selectedElement;
    const description = sourceData?.description;
    if (!description || description.trim() === '') {
      return this.translate.instant('MODEL_DIALOG.INCLUDE_DESCRIPTION');
    }
    const truncated = description.length > 32 ? description.substring(0, 29) + '...' : description;
    return this.translate.instant('MODEL_DIALOG.INCLUDE_DESCRIPTION_WITH_VALUE', { description: truncated });
  }

  create(): void {
    if (!this.myForm.valid || this.isCreating) return;

    this.isCreating = true;
    const formValue = this.myForm.value;

    const inclusionOptions: InclusionOptions = {
      includeName: formValue.includeName,
      includeDescription: formValue.includeDescription,
      includeProperties: formValue.includeProperties,
      includePropertyValues: formValue.includePropertyValues,
      includeStyles: formValue.includeStyles,
      eraseUnrelatedProperties: formValue.eraseUnrelatedProperties
    };

    if (this.isEditMode) {
      // Update existing model
      const updateData: UpdateModelDto = {
        name: formValue.name,
        description: formValue.description,
        inclusionOptions
      };

      // MAP-1569: toggle is ON (and still visible/valid) - also replace the
      // model's captured element (name, description, properties, property
      // values, styles) with the current selection focus, regardless of the
      // inclusion toggles above.
      if (formValue.updateFromSelectionFocus && this.selectionFocusMatchesModelType()) {
        const elementId = this.getSelectionFocusElementId();
        if (elementId) {
          updateData.elementId = elementId;
        }
      }

      this.modelService.update(this.data.model!.id, updateData).subscribe({
        next: (model: ModelDto) => {
          // Update legend inclusion if changed
          const mapId = this.data.mapId || this.mapService.getCurrentSelectedMapFromStore()?.id;
          if (mapId) {
            const excluded = formValue.excludeFromLegend;
            this.legendService.toggleModelInclusion(model.id, mapId, !excluded).subscribe();
          }
          this.dialogRef.close(model);
        },
        error: (error) => {
          console.error('Error updating model:', error);
          this.notificationService.error(this.translate.instant('MODEL_DIALOG.UPDATE_ERROR', { message: error.error?.message || error.message }));
          this.isCreating = false;
        }
      });
    } else {
      // Create new model
      const createModelDto: CreateModelDto = {
        name: formValue.name,
        description: formValue.description,
        elementType: this.data.elementType!,
        elementId: this.data.selectedElement.id,
        inclusionOptions,
        mapId: this.data.mapId!
      };

      this.modelService.save(createModelDto).subscribe({
        next: (model: ModelDto) => {
          // If user chose to exclude from legend, toggle it off
          if (formValue.excludeFromLegend && this.data.mapId) {
            this.legendService.toggleModelInclusion(model.id, this.data.mapId, false).subscribe();
          }
          this.dialogRef.close(model);
        },
        error: (error) => {
          console.error('Error creating model:', error);
          this.notificationService.error(this.translate.instant('MODEL_DIALOG.CREATE_ERROR', { message: error.error?.message || error.message }));
          this.isCreating = false;
        }
      });
    }
  }
}
